import { Center, Loader } from '@mantine/core';
import React, { Suspense } from 'react';
import { Routes, Route, useParams } from 'react-router-dom';

import NotFound from 'components/NotFound';
import {
  AcceptInviteForm,
  LoginForm,
  RegisterForm,
  RequestAccessForm,
  RequireAuth,
} from 'features/auth';
import { LandingPage, PublicLayout } from 'features/landing';
import { lazyWithChunkReload } from 'utils/lazyWithChunkReload';

/* ─── Lazy: Public pages ─── */
const HowItWorksPage = lazyWithChunkReload(() =>
  import('features/landing/pages/HowItWorksPage').then((m) => ({ default: m.default }))
);
const FeaturesPage = lazyWithChunkReload(() =>
  import('features/landing/pages/FeaturesPage').then((m) => ({ default: m.default }))
);
const PricingPage = lazyWithChunkReload(() =>
  import('features/landing/pages/PricingPage').then((m) => ({ default: m.default }))
);
const IntegrationsPage = lazyWithChunkReload(() =>
  import('features/landing/pages/IntegrationsPage').then((m) => ({ default: m.default }))
);
const TestimonialsPage = lazyWithChunkReload(() =>
  import('features/landing/pages/TestimonialsPage').then((m) => ({ default: m.default }))
);
const AboutUsPage = lazyWithChunkReload(() =>
  import('features/landing/pages/AboutUsPage').then((m) => ({ default: m.default }))
);
const ContactUsPage = lazyWithChunkReload(() =>
  import('features/landing/pages/ContactUsPage').then((m) => ({ default: m.default }))
);
const PrivacyPolicyPage = lazyWithChunkReload(() =>
  import('features/landing/pages/PrivacyPolicyPage').then((m) => ({ default: m.default }))
);
const TermsAndConditionsPage = lazyWithChunkReload(() =>
  import('features/landing/pages/TermsAndConditionsPage').then((m) => ({ default: m.default }))
);
const CookiePolicyPage = lazyWithChunkReload(() =>
  import('features/landing/pages/CookiePolicyPage').then((m) => ({ default: m.default }))
);
const UseCasesPage = lazyWithChunkReload(() =>
  import('features/landing/pages/UseCasesPage').then((m) => ({ default: m.default }))
);

/* ─── Lazy: App pages ─── */
const AdminSettings = lazyWithChunkReload(() =>
  import('features/admin-settings').then((m) => ({ default: m.AdminSettings }))
);
const BacklogBoard = lazyWithChunkReload(() =>
  import('features/backlog').then((m) => ({ default: m.BacklogBoard }))
);
const CalendarView = lazyWithChunkReload(() =>
  import('features/calendar/components/CalendarView').then((m) => ({
    default: m.default,
  }))
);
const CrossProjectRoadmapView = lazyWithChunkReload(() =>
  import('features/cross-project-roadmap').then((m) => ({
    default: m.CrossProjectRoadmapView,
  }))
);
const DashboardsList = lazyWithChunkReload(() =>
  import('features/dashboards').then((m) => ({ default: m.DashboardsList }))
);
const DashboardView = lazyWithChunkReload(() =>
  import('features/dashboards').then((m) => ({ default: m.DashboardView }))
);
const GroupsManagement = lazyWithChunkReload(() =>
  import('features/dashboards/components/GroupsManagement').then((m) => ({
    default: m.default,
  }))
);
const RoleForm = lazyWithChunkReload(() =>
  import('features/dashboards/components/RoleManagement/RoleForm').then((m) => ({
    default: m.default,
  }))
);
const RoleTable = lazyWithChunkReload(() =>
  import('features/dashboards/components/RoleManagement/RoleTable').then((m) => ({
    default: m.default,
  }))
);
const DependencyGraph = lazyWithChunkReload(() =>
  import('features/dependency-graph/components/DependencyGraph').then((m) => ({
    default: m.default,
  }))
);
const HelpPage = lazyWithChunkReload(() =>
  import('features/help').then((m) => ({ default: m.HelpPage }))
);
const KanbanBoard = lazyWithChunkReload(() =>
  import('features/kanban-board').then((m) => ({ default: m.KanbanBoard }))
);
const ScrumBoard = lazyWithChunkReload(() =>
  import('features/kanban-board').then((m) => ({ default: m.ScrumBoard }))
);
const ListView = lazyWithChunkReload(() =>
  import('features/list-view/components/ListView').then((m) => ({
    default: m.default,
  }))
);
const MyTasks = lazyWithChunkReload(() =>
  import('features/my-tasks').then((m) => ({ default: m.MyTasks }))
);
const OverviewDashboard = lazyWithChunkReload(() =>
  import('features/overview-dashboard').then((m) => ({
    default: m.OverviewDashboard,
  }))
);
const PortfolioView = lazyWithChunkReload(() =>
  import('features/portfolio').then((m) => ({ default: m.PortfolioView }))
);
const ProjectDashboard = lazyWithChunkReload(() =>
  import('features/project-dashboard').then((m) => ({
    default: m.ProjectDashboard,
  }))
);
const DocPageView = lazyWithChunkReload(() =>
  import('features/project-docs/components').then((m) => ({
    default: m.DocPageView,
  }))
);
const DocsLayout = lazyWithChunkReload(() =>
  import('features/project-docs/components').then((m) => ({
    default: m.DocsLayout,
  }))
);
const DocsView = lazyWithChunkReload(() =>
  import('features/project-docs/components').then((m) => ({
    default: m.DocsView,
  }))
);
const ProjectManagement = lazyWithChunkReload(() =>
  import('features/project-management').then((m) => ({
    default: m.ProjectManagement,
  }))
);
const ReportsPage = lazyWithChunkReload(() =>
  import('features/reports').then((m) => ({ default: m.ReportsPage }))
);
const ResourceView = lazyWithChunkReload(() =>
  import('features/resource-view').then((m) => ({ default: m.ResourceView }))
);
const RoadmapViewWrapper = lazyWithChunkReload(() =>
  import('features/roadmap/components/RoadmapViewWrapper').then((m) => ({
    default: m.default,
  }))
);
const TeamActivity = lazyWithChunkReload(() =>
  import('features/team-activity').then((m) => ({ default: m.TeamActivity }))
);
const Welcome = lazyWithChunkReload(() =>
  import('features/user').then((m) => ({ default: m.Welcome }))
);

function DependencyGraphWrapper() {
  const { projectId } = useParams<{ projectId: string }>();
  if (!projectId) {
    return null;
  }
  return <DependencyGraph projectId={projectId} />;
}

function RouteFallback() {
  return (
    <Center py="xl">
      <Loader size="lg" />
    </Center>
  );
}

function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* ─── Landing page (with transparent header) ─── */}
        <Route element={<PublicLayout headerVariant="transparent" />}>
          <Route path="/" element={<LandingPage />} />
        </Route>

        {/* ─── Public pages (with solid header) ─── */}
        <Route element={<PublicLayout />}>
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/integrations" element={<IntegrationsPage />} />
          <Route path="/use-cases" element={<UseCasesPage />} />
          <Route path="/testimonials" element={<TestimonialsPage />} />
          <Route path="/about" element={<AboutUsPage />} />
          <Route path="/contact" element={<ContactUsPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsAndConditionsPage />} />
          <Route path="/cookies" element={<CookiePolicyPage />} />
        </Route>

        {/* ─── Auth pages (no layout) ─── */}
        <Route path="/login" element={<LoginForm />} />
        <Route path="/register" element={<RegisterForm />} />
        <Route path="/invite" element={<AcceptInviteForm />} />
        <Route path="/welcome" element={<Welcome />} />
        <Route path="/request-access" element={<RequestAccessForm />} />

        {/* ─── Authenticated pages ─── */}
        <Route element={<RequireAuth />}>
          <Route path="/request-access" element={<RequestAccessForm />} />
          <Route path="/dashboard" element={<OverviewDashboard />} />
          <Route path="/dashboard/:projectId" element={<ProjectDashboard />} />
          <Route path="/projects/:projectId" element={<KanbanBoard />} />
          <Route path="/projects/:projectId/scrum" element={<ScrumBoard />} />
          <Route path="/projects/:projectId/backlog" element={<BacklogBoard />} />
          <Route
            path="/projects/:projectId/roadmap"
            element={<RoadmapViewWrapper />}
          />
          <Route
            path="/projects/:projectId/calendar"
            element={<CalendarView />}
          />
          <Route path="/projects/:projectId/list" element={<ListView />} />
          <Route
            path="/projects/:projectId/dependencies"
            element={<DependencyGraphWrapper />}
          />
          <Route path="/projects/:projectId/reports" element={<ReportsPage />} />
          <Route path="/projects/:projectId/docs" element={<DocsLayout />}>
            <Route index element={<DocsView />} />
            <Route path=":pageId" element={<DocPageView />} />
          </Route>
          <Route path="/portfolio" element={<PortfolioView />} />
          <Route
            path="/cross-project-roadmap"
            element={<CrossProjectRoadmapView />}
          />
          <Route path="/resource-view" element={<ResourceView />} />
          <Route path="/project-management" element={<ProjectManagement />} />
          <Route path="/team-activity" element={<TeamActivity />} />
          <Route path="/admin-settings" element={<AdminSettings />} />
          <Route path="/roles" element={<RoleTable />} />
          <Route path="/roles/add" element={<RoleForm />} />
          <Route path="/roles/edit/:roleId" element={<RoleForm />} />
          <Route path="/my-tasks" element={<MyTasks />} />
          <Route path="/dashboards" element={<DashboardsList />} />
          <Route path="/dashboards/:dashboardId" element={<DashboardView />} />
          <Route path="/groups" element={<GroupsManagement />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

export default AppRoutes;
